import { AnimatePresence, motion } from 'framer-motion';
import { SunMoon } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next'; // Import useTranslation
import { useLocation, useNavigate } from 'react-router';
import QrScannerEnhanced from '../../components/qr-scanner-enhanced';
import urlValidationService, { ValidationResult } from '../../services/url-validation-service';

/**
 * Enhanced Scan Page Component
 *
 * This page provides an improved QR code scanning experience for the museum app.
 * It features:
 * - Sleek, intuitive interface with prominent viewfinder
 * - Subtle animations for visual feedback
 * - Automatic brightness adjustment
 * - Haptic feedback on successful scans
 * - Intelligent error handling with actionable solutions
 * - Gallery import option for scanning from saved images
 * - Accessibility enhancements including high contrast mode
 */
const EnhancedScanPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation(); // Initialize translation hook
    const [isValidating, setIsValidating] = useState<boolean>(false);
    const [isScanning, setIsScanning] = useState<boolean>(false);
    const [isScannerActive, setIsScannerActive] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [scanResult, setScanResult] = useState<string | null>(null);
    const [cameraPermission, setCameraPermission] = useState<boolean | null>(null);
    const [scanHistory, setScanHistory] = useState<string[]>([]);
    const [highContrastMode, setHighContrastMode] = useState<boolean>(false);

    // Reference to track if component is mounted
    const isMounted = useRef(true);

    // Reference to track active media streams
    const activeStreams = useRef<MediaStream[]>([]);

    // Function to stop all active camera streams
    const stopAllCameraStreams = () => {
        console.log('Stopping all camera streams');
        activeStreams.current.forEach((stream) => {
            stream.getTracks().forEach((track) => {
                console.log('Stopping track:', track.kind, track.label);
                track.stop();
            });
        });
        activeStreams.current = [];
    };

    // Start scanning when the component mounts
    useEffect(() => {
        // Set mounted flag
        isMounted.current = true;

        // Request camera permission
        navigator.mediaDevices
            .getUserMedia({ video: true })
            .then((stream) => {
                if (isMounted.current) {
                    // Store the stream for cleanup
                    activeStreams.current.push(stream);

                    setCameraPermission(true);
                    setIsScanning(true);
                    setIsScannerActive(true);
                } else {
                    // If component unmounted before promise resolved, stop the stream
                    stream.getTracks().forEach((track) => track.stop());
                }
            })
            .catch((err) => {
                console.error('Camera permission denied:', err);
                if (isMounted.current) {
                    setCameraPermission(false);
                    setError(t('scan.errorCameraDenied', 'Camera access denied. Please allow camera access to scan QR codes.'));
                }
            });

        // Clean up when component unmounts
        return () => {
            console.log('Scan page unmounting, closing camera');
            isMounted.current = false;
            setIsScannerActive(false);
            setIsScanning(false);

            // Force stop all camera streams
            stopAllCameraStreams();

            // Force stop any video elements
            const videoElements = document.querySelectorAll('video');
            videoElements.forEach((video) => {
                if (video.srcObject) {
                    const stream = video.srcObject as MediaStream;
                    stream.getTracks().forEach((track) => {
                        console.log('Stopping video track:', track.kind, track.label);
                        track.stop();
                    });
                    video.srcObject = null;
                }
            });
        };
    }, []);

    // Handle page visibility changes to stop camera when page is hidden
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                console.log('Page hidden, deactivating scanner');
                setIsScannerActive(false);
            } else {
                console.log('Page visible, activating scanner');
                if (isMounted.current) {
                    setIsScannerActive(true);
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    // Handle navigation away from the page
    useEffect(() => {
        const handleBeforeUnload = () => {
            console.log('Page unloading, closing camera');
            setIsScannerActive(false);
            stopAllCameraStreams();
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    /**
     * Handle successful QR code scan
     * @param decodedText - The decoded text from the QR code
     */
    const handleScanSuccess = (decodedText: string) => {
        console.log('Scan success:', decodedText);
        setScanResult(decodedText);
        setIsValidating(true);
        setError(null);

        // Add to scan history
        setScanHistory((prev) => {
            const newHistory = [decodedText, ...prev];
            // Keep only the last 10 scans
            return newHistory.slice(0, 10);
        });

        // Validate the URL
        const validationResult = urlValidationService.validateUrl(decodedText);

        if (validationResult.isValid) {
            // Stop the scanner before navigating
            setIsScannerActive(false);
            stopAllCameraStreams();

            // Navigate to the URL if it's valid
            handleValidUrl(validationResult);
        } else {
            // Show error if URL is invalid
            handleInvalidUrl(validationResult);
        }

        setIsValidating(false);
    };

    /**
     * Handle valid URL
     * @param validationResult - The validation result
     */
    const handleValidUrl = (validationResult: ValidationResult) => {
        try {
            const url = new URL(validationResult.url);

            // Extract the path from the URL
            const path = url.pathname;

            // Stop all camera streams before navigating
            stopAllCameraStreams();

            // Navigate to the path
            navigate(path);
        } catch (error) {
            setError(t('scan.errorNavigationFailed', 'Failed to navigate to the scanned URL'));
            setIsValidating(false); // Set validating false on navigation error
        }
    };

    /**
     * Handle invalid URL
     * @param validationResult - The validation result
     */
    const handleInvalidUrl = (validationResult: ValidationResult) => {
        setError(validationResult.errorMessage || t('scan.errorInvalidCode', 'Invalid QR code'));
        // Make sure to clear the scan result when there's an error
        // This prevents the success message from showing when retrying
        setScanResult(null);
    };

    /**
     * Handle scan error
     * @param errorMessage - The error message
     */
    const handleScanError = (errorMessage: string) => {
        console.error('Scan error:', errorMessage);
        setError(errorMessage);
        setIsScanning(false);
        // Clear scan result on error
        setScanResult(null);
    };

    /**
     * Retry scanning after an error
     */
    const handleRetry = () => {
        console.log('Retrying scan...');
        // Clear both error and scan result states
        setError(null);
        setScanResult(null);
        setIsScanning(true);
        setIsScannerActive(true);

        // Re-request camera permission if needed
        if (!cameraPermission) {
            navigator.mediaDevices
                .getUserMedia({ video: true })
                .then((stream) => {
                    if (isMounted.current) {
                        // Store the stream for cleanup
                        activeStreams.current.push(stream);

                        setCameraPermission(true);
                    } else {
                        // If component unmounted before promise resolved, stop the stream
                        stream.getTracks().forEach((track) => track.stop());
                    }
                })
                .catch((err) => {
                    console.error('Camera permission denied again:', err);
                    if (isMounted.current) {
                        setCameraPermission(false);
                        setError(t('scan.errorCameraDenied', 'Camera access denied. Please allow camera access to scan QR codes.'));
                    }
                });
        }
    };

    /**
     * Toggle high contrast mode for accessibility
     */
    const toggleHighContrastMode = () => {
        setHighContrastMode((prev) => !prev);
    };

    return (
        <div className="enhanced-scan-page h-full">
            <div className="mx-auto flex max-w-2xl flex-col">
                {/* Header */}
                <div className="z-10 bg-white p-4 shadow-sm dark:bg-neutral-800">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-2xl font-bold">{t('scan.title', 'Scan QR Code')}</h1>
                            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                                {t('scan.description', 'Point your camera at a museum QR code')}
                            </p>
                        </div>
                        <button
                            onClick={toggleHighContrastMode}
                            className={`rounded-full p-2 ${highContrastMode ? 'bg-yellow-400 text-black' : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'}`}
                            aria-label={
                                highContrastMode
                                    ? t('scan.ariaDisableHighContrast', 'Disable high contrast mode')
                                    : t('scan.ariaEnableHighContrast', 'Enable high contrast mode')
                            }
                        >
                            <SunMoon size={20} />
                        </button>
                    </div>
                </div>

                {/* Scanner component - takes up most of the screen */}
                <div className="relative flex flex-grow flex-col">
                    {/* Scanner overlay */}
                    {isScanning && cameraPermission && (
                        <QrScannerEnhanced
                            onScanSuccess={handleScanSuccess}
                            onScanError={handleScanError}
                            width="100%"
                            height="100%"
                            showBorder={false}
                            isActive={isScannerActive}
                            showFlashlight={true}
                            showGalleryImport={true}
                            enableHapticFeedback={true}
                            enableBrightnessAdjustment={true}
                            highContrastMode={highContrastMode}
                        />
                    )}

                    {/* Camera permission denied message */}
                    {cameraPermission === false && (
                        <div className="flex h-full items-center justify-center bg-neutral-900 p-6 text-center text-white">
                            <div>
                                <svg
                                    className="mx-auto mb-4 h-16 w-16 text-red-500"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                    />
                                </svg>
                                <h3 className="mb-2 text-lg font-medium">{t('scan.permissionDeniedTitle', 'Camera Access Required')}</h3>
                                <p className="mb-4">{t('scan.permissionDeniedDescription', 'Please allow camera access to scan QR codes.')}</p>
                                <button className="rounded-md bg-blue-600 px-4 py-2 text-white" onClick={handleRetry}>
                                    {t('common.tryAgain', 'Try Again')}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Loading indicator */}
                    {cameraPermission === null && (
                        <div className="flex h-full items-center justify-center bg-neutral-900 text-white">
                            <div className="text-center">
                                <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-t-blue-500 border-r-transparent border-b-transparent border-l-transparent"></div>
                                <p>{t('scan.loadingCamera', 'Opening camera...')}</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Status messages */}
                <div className="bg-white p-4 shadow-lg dark:bg-neutral-800">
                    {/* Validation status */}
                    <AnimatePresence>
                        {isValidating && (
                            <motion.div
                                className="mb-2 rounded-md bg-blue-50 p-3 dark:bg-blue-900/30"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                            >
                                <div className="flex items-center">
                                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-blue-600 dark:border-blue-400"></div>
                                    <p className="text-blue-600 dark:text-blue-400">{t('scan.statusValidating', 'Validating QR code...')}</p>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Error message */}
                    <AnimatePresence>
                        {error && (
                            <motion.div
                                className="mb-2 rounded-md bg-red-50 p-3 dark:bg-red-900/30"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                            >
                                <p className="font-medium text-red-600 dark:text-red-400">
                                    {t('common.errorPrefix', 'Error:')} {error}
                                </p>{' '}
                                {/* Keep error variable */}
                                <p className="mt-1 text-sm text-red-500 dark:text-red-300">
                                    {t('scan.errorScanValidCode', 'Please scan a valid museum QR code.')}
                                </p>
                                {cameraPermission !== false && (
                                    <button className="mt-2 rounded-md bg-red-600 px-3 py-1 text-xs text-white" onClick={handleRetry}>
                                        {t('common.tryAgain', 'Try Again')}
                                    </button>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Success message - only show if there's a scan result and no error */}
                    <AnimatePresence>
                        {scanResult && !error && !isValidating && (
                            <motion.div
                                className="mb-2 rounded-md bg-green-50 p-3 dark:bg-green-900/30"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                            >
                                <p className="font-medium text-green-600 dark:text-green-400">{t('scan.successTitle', 'Successfully scanned!')}</p>
                                <p className="mt-1 text-sm text-green-500 dark:text-green-300">
                                    {t('scan.successDescription', 'Navigating to the content...')}
                                </p>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Scanning tips */}
                    <div className="mt-2">
                        <h2 className="mb-1 text-sm font-medium">{t('scan.tipsTitle', 'Scanning Tips:')}</h2>
                        <ul className="space-y-1 text-xs text-gray-600 dark:text-gray-400">
                            <li>{t('scan.tip1', '• Make sure the QR code is well-lit and clearly visible')}</li>
                            <li>{t('scan.tip2', '• Hold your device steady while scanning')}</li>
                            <li>{t('scan.tip3', '• Position the QR code within the scanning frame')}</li>
                            <li>{t('scan.tip4', '• Use the flashlight in low-light conditions')}</li>
                        </ul>
                    </div>
                </div>

                {/* Add custom styles for animations */}
                <style>
                    {`
                @keyframes scanline {
                    0% {
                        transform: translateY(-100px);
                        opacity: 0;
                    }
                    50% {
                        opacity: 1;
                    }
                    100% {
                        transform: translateY(100px);
                        opacity: 0;
                    }
                }
                .animate-scanline {
                    animation: scanline 2s linear infinite;
                }
                `}
                </style>
            </div>
        </div>
    );
};

export default EnhancedScanPage;
